import { useState, useCallback } from "react";

import type { FilterTypes, FilterTypeValues } from "../TableFilters.type";
import { usePaginationOffset } from "@certa/common/hooks/usePaginationOffset";
import type { SortDirection } from "../../Table.types";

// Reusable type utility to enforce tuple types (reject mutable arrays)
type EnsureTuple<T> = T extends readonly unknown[]
  ? T["length"] extends number
    ? T extends unknown[]
      ? never
      : T
    : never
  : never;

// Extended filters state that supports both known and dynamic columns
export type FiltersState<T extends FilterField> = {
  readonly [K in T["name"]]: FilterTypeValues[Extract<T, { name: K }>["type"]];
};

// Extended dynamic filters state for runtime usage - supports both known and unknown columns
type DynamicFiltersState = Record<
  string,
  FilterTypeValues[FilterTypes] | undefined
>;

// Return type for filters that provides proper typing for tuple columns
type TypedFiltersReturn<T extends FilterField> = {
  readonly [K in T["name"]]?: FilterTypeValues[Extract<T, { name: K }>["type"]];
} & {
  readonly [key: string]: FilterTypeValues[FilterTypes] | undefined;
};

// Extended handler type that accepts both known columns and dynamic strings
type FilterChangeHandlerType<T extends FilterField> = <
  K extends T["name"] | string
>(
  columnName: K,
  value: K extends T["name"]
    ? FilterTypeValues[Extract<T, { name: K }>["type"]] | undefined
    : FilterTypeValues[FilterTypes] | undefined
) => void;

export type FilterChangeHandlerFromFields<T extends readonly FilterField[]> =
  FilterChangeHandlerType<T[number]>;

export type SortState<TColumnId extends string = string> = {
  readonly direction: SortDirection;
  readonly columnId: TColumnId;
};

export type FilterField = {
  readonly name: string;
  readonly type: FilterTypes;
  readonly initialValue?: FilterTypeValues[FilterTypes];
};

export type UseTableFiltersProps<
  TFilter extends readonly FilterField[],
  TColumns extends readonly [string, ...string[]]
> = {
  readonly filters: {
    readonly columns: EnsureTuple<TFilter>;
    readonly initialState?: DynamicFiltersState;
  };
  readonly sort?: {
    readonly columns: EnsureTuple<TColumns>;
    readonly initialState?: SortState<TColumns[number] | string>;
  };
};

export function useTableFilters<
  TFilter extends readonly FilterField[],
  TColumns extends readonly [string, ...string[]]
>(props: UseTableFiltersProps<TFilter, TColumns>) {
  const { filters: filtersProps, sort: sortProps } = props;
  const { columns } = filtersProps;

  const [filters, setFilters] = useState<DynamicFiltersState>(() => {
    const initialFiltersState = Array.from(columns).reduce<DynamicFiltersState>(
      (acc, column) => {
        if (column.initialValue !== undefined) {
          const name = column.name;
          const value = column.initialValue;
          return { ...acc, [name]: value };
        }
        return acc;
      },
      { ...filtersProps.initialState }
    );
    return initialFiltersState;
  });

  const [sort, setSort] = useState<
    SortState<TColumns[number] | string> | undefined
  >(sortProps?.initialState);
  const [currentPage, setCurrentPage] = useState(1);
  const offset = usePaginationOffset(currentPage);

  const orderBy = sort
    ? sort.direction === "desc"
      ? `-${sort.columnId}`
      : sort.columnId
    : undefined;

  const handleSortChange = useCallback(
    (columnId: TColumns[number] | string, direction?: SortDirection) => {
      setCurrentPage(1);
      if (direction) setSort({ columnId, direction });
      else setSort(sortProps?.initialState);
    },
    [sortProps?.initialState]
  );

  const handleFilterChange = useCallback<
    FilterChangeHandlerType<TFilter[number]>
  >((columnName, value) => {
    setCurrentPage(1);
    setFilters((prev: DynamicFiltersState) => {
      if (value === undefined || value === "") {
        const newState = { ...prev };
        delete newState[columnName];
        return newState;
      }
      return { ...prev, [columnName]: value };
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setCurrentPage(1);
    setSort(sortProps?.initialState);
  }, [sortProps?.initialState]);

  return {
    filters: filters as TypedFiltersReturn<TFilter[number]>,
    sort: sort
      ? {
          columnId: sort.columnId,
          direction: sort.direction,
          orderBy
        }
      : undefined,
    currentPage,
    offset,
    onSortChange: handleSortChange,
    onFilterChange: handleFilterChange,
    onClearFilters: handleClearFilters,
    setCurrentPage
  } as const;
}
